import {
  Home,
  ShoppingCart,
  Calendar,
  Truck,
  FileText,
  Wrench,
  BarChart3,
  Users,
  MapPin,
  Building2,
  Package,
  Layers,
  Settings,
  DollarSign,
  Activity,
  type LucideIcon,
} from "lucide-react";

// =============================================================================
// TYYPIT
// =============================================================================

/**
 * Yksittäinen navigaatiolinkki
 */
export interface NavLink {
  to: string;
  label: string;
  icon: LucideIcon;
  description?: string;
}

/**
 * Navigaatioryhmä (dropdown-valikko)
 */
export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  links: NavLink[];
}

// =============================================================================
// ADMIN-NAVIGAATIO
// =============================================================================

/**
 * Suorat linkit - näkyvät aina päänavigaatiossa
 * Nämä ovat useimmin käytetyt toiminnot
 */
export const ADMIN_DIRECT_LINKS: NavLink[] = [
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: Home,
  },
  {
    to: "/orders",
    label: "Tilaukset",
    icon: ShoppingCart,
  },
  {
    to: "/calendar",
    label: "Kalenteri",
    icon: Calendar,
  },
];

/**
 * Ryhmitellyt linkit - näkyvät dropdown-valikoissa
 */
export const ADMIN_NAV_GROUPS: NavGroup[] = [
  {
    id: "production",
    label: "Tuotanto",
    icon: Truck,
    links: [
      {
        to: "/loads",
        label: "Kuormat",
        icon: Truck,
        description: "Kuljetusten suunnittelu ja seuranta",
      },
      {
        to: "/cutting-list",
        label: "Sahauslista",
        icon: FileText,
        description: "Päivän sahaustyöt ja työjono",
      },
      {
        to: "/installations",
        label: "Asennukset",
        icon: Wrench,
        description: "Asennusten hallinta ja seuranta",
      },
      {
        to: "/reports",
        label: "Raportit",
        icon: BarChart3,
        description: "Tilastot, analytiikka ja tulosteet",
      },
    ],
  },
  {
    id: "registers",
    label: "Rekisterit",
    icon: Users,
    links: [
      {
        to: "/customers",
        label: "Asiakkaat",
        icon: Users,
        description: "Asiakasrekisterin hallinta",
      },
      {
        to: "/cemeteries",
        label: "Hautausmaat",
        icon: MapPin,
        description: "Hautausmaiden tiedot ja yhteyshenkilöt",
      },
      {
        to: "/partners",
        label: "Kumppanit",
        icon: Building2,
        description: "Asentajat ja yhteistyökumppanit",
      },
    ],
  },
  {
    id: "settings",
    label: "Asetukset",
    icon: Settings,
    links: [
      {
        to: "/products",
        label: "Tuotteet",
        icon: Package,
        description: "Tuotekatalogi ja perushinnat",
      },
      {
        to: "/materials",
        label: "Materiaalit",
        icon: Layers,
        description: "Kivilaadut ja tarvikkeet",
      },
      {
        to: "/configuration",
        label: "Tuotemallit",
        icon: Settings,
        description: "Konfiguraattorin tuotemallit",
      },
      {
        to: "/configuration/pricing",
        label: "Hintasäännöt",
        icon: DollarSign,
        description: "Hinnoittelulogiikka ja säännöt",
      },
      {
        to: "/configuration/email-templates",
        label: "Viestimallit",
        icon: FileText,
        description: "Sähköpostien ja hyväksyntäsivujen tekstit",
      },
      {
        to: "/users",
        label: "Käyttäjät",
        icon: Users,
        description: "Käyttäjien ja roolien hallinta",
      },
      {
        to: "/api-usage",
        label: "API-käyttö",
        icon: Activity,
        description: "SMS- ja sähköpostipalveluiden käyttöseuranta",
      },
    ],
  },
];

// =============================================================================
// ASENTAJA-NAVIGAATIO
// =============================================================================

/**
 * Asentajan suorat linkit - yksinkertaisempi navigaatio
 */
export const INSTALLER_DIRECT_LINKS: NavLink[] = [
  {
    to: "/installer",
    label: "Etusivu",
    icon: Home,
  },
  {
    to: "/loads/mine",
    label: "Kuormat",
    icon: Truck,
  },
  {
    to: "/installations/mine",
    label: "Asennukset",
    icon: Wrench,
  },
];

/**
 * Asentajalla ei ole dropdown-ryhmiä
 */
export const INSTALLER_NAV_GROUPS: NavGroup[] = [];

// =============================================================================
// APUFUNKTIOT
// =============================================================================

/**
 * Tarkista onko polku aktiivinen
 * Tukee myös alipolkuja (esim. /orders/123 on aktiivinen kun linkki on /orders)
 */
export function isPathActive(currentPath: string, linkPath: string): boolean {
  // Dashboard ja installer ovat aktiivisia vain täsmälleen
  if (linkPath === "/dashboard" || linkPath === "/installer") {
    return currentPath === linkPath;
  }
  // Muut linkit aktiivisia myös alipolkujen kanssa
  return currentPath === linkPath || currentPath.startsWith(`${linkPath}/`);
}

/**
 * Tarkista onko jokin ryhmän linkeistä aktiivinen
 */
export function isGroupActive(currentPath: string, group: NavGroup): boolean {
  return group.links.some((link) => isPathActive(currentPath, link.to));
}

/**
 * Palauttaa kaikki linkit listana (suorat + ryhmien linkit)
 * Käytetään mobiilinavigaatiossa
 */
export function getAllLinksFlat(
  directLinks: NavLink[],
  groups: NavGroup[]
): { link: NavLink; groupLabel?: string }[] {
  const result: { link: NavLink; groupLabel?: string }[] = [];

  // Lisää suorat linkit
  directLinks.forEach((link) => {
    result.push({ link });
  });

  // Lisää ryhmien linkit
  groups.forEach((group) => {
    group.links.forEach((link) => {
      result.push({ link, groupLabel: group.label });
    });
  });

  return result;
}
